/**
 * QuoteInfoSection - Basic information for quote/template
 *
 * For Quotes: Customer, Title, Description
 * For Templates: Name, Description
 */

import { useTranslation } from "react-i18next";
import React from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QuoteFormData, TemplateFormData } from "../index";
import type { Customer } from "@onsemble/shared";
import { getCustomerDisplayName } from "@shared/customer-utils";

interface QuoteInfoSectionProps {
  form: UseFormReturn<QuoteFormData | TemplateFormData>;
  entityType: "quote" | "template";
  customers?: Customer[];
  onCreateCustomer?: () => void;
}

export const QuoteInfoSection: React.FC<QuoteInfoSectionProps> = ({
  form,
  entityType,
  customers = [],
  onCreateCustomer,
}) => {
  // Handle customer selection, including "Create New Customer" option
  const { t } = useTranslation();
  const handleCustomerChange = (value: string) => {
    if (value === "create-new") {
      onCreateCustomer?.();
    } else {
      form.setValue("customerId" as any, value, {
        shouldValidate: true,
        shouldDirty: true,
      });
      form.clearErrors("customerId" as any);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium mb-4">
          {entityType === "quote" ? t("features.quotes.quoteEditor.sections.quoteInfoSection.quote") : t("common.template")} {t("features.quotes.quoteEditor.sections.quoteInfoSection.information")}
        </h3>

        <div className="space-y-4">
          {/* Customer Selector (Quotes only) */}
          {entityType === "quote" && (
            <FormField
              control={form.control}
              name={"customerId" as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("features.quotes.quoteEditor.sections.quoteInfoSection.customer")}</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={handleCustomerChange}
                      value={field.value || ""}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("features.quotes.quoteEditor.sections.quoteInfoSection.selectACustomer")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value="create-new"
                          className="text-blue-600 font-medium"
                        >
                          {t("features.quotes.quoteEditor.sections.quoteInfoSection.createNewCustomer")}
                        </SelectItem>
                        {customers && customers.length > 0 ? (
                          customers.map((customer) => (
                            <SelectItem key={customer.uid} value={customer.uid}>
                              {getCustomerDisplayName(customer)}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-customers" disabled>
                            {t("features.quotes.quoteEditor.sections.quoteInfoSection.noCustomersFound")}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Template Name (Templates only) */}
          {entityType === "template" && (
            <FormField
              control={form.control}
              name={"name" as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("features.quotes.quoteEditor.sections.quoteInfoSection.templateName")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("features.quotes.quoteEditor.sections.quoteInfoSection.enterTemplateName")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Title (both quotes and templates) */}
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("features.quotes.quoteEditor.sections.quoteInfoSection.title")}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={`Enter ${entityType} title`} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("features.quotes.quoteEditor.sections.quoteInfoSection.description")}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={`Enter ${entityType} description`}
                    rows={4}
                    className="resize-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};
